import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { experimental_extendTheme } from '@mui/material/styles';

type ColorScheme = 'blue' | 'green' | 'purple' | 'orange';
type ChartType = 'bar' | 'line' | 'area';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  chartType: ChartType;
  setChartType: (type: ChartType) => void;
  theme: ReturnType<typeof experimental_extendTheme>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const colorSchemes: Record<ColorScheme, { primary: string; secondary: string }> = {
  blue: { primary: '#1e76cf', secondary: '#9c4146' },
  green: { primary: '#2e7d32', secondary: '#c62828' },
  purple: { primary: '#7b1fa2', secondary: '#e91e63' },
  orange: { primary: '#f57c00', secondary: '#d32f2f' },
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    // Always default to blue, ignore saved value if it's green
    const saved = localStorage.getItem('colorScheme') as ColorScheme;
    if (saved === 'green') {
      localStorage.removeItem('colorScheme');
      return 'blue';
    }
    return saved || 'blue';
  });

  const [chartType, setChartTypeState] = useState<ChartType>(() => {
    const saved = localStorage.getItem('chartType') as ChartType;
    return saved || 'bar';
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('colorScheme', colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    localStorage.setItem('chartType', chartType);
  }, [chartType]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev: boolean) => !prev);
  };

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
  };

  const setChartType = (type: ChartType) => {
    setChartTypeState(type);
  };

  const colors = colorSchemes[colorScheme];

  const theme = experimental_extendTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: colors.primary,
          },
          secondary: {
            main: colors.secondary,
          },
          background: {
            default: '#f8f9fa',
            paper: '#ffffff',
          },
        },
      },
      dark: {
        palette: {
          primary: {
            main: colors.primary,
          },
          secondary: {
            main: colors.secondary,
          },
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
        },
      },
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    shape: {
      borderRadius: 16,
    },
    cssVarPrefix: 'aeo',
  });

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleDarkMode,
        colorScheme,
        setColorScheme,
        chartType,
        setChartType,
        theme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
